import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { UserLogin } from '../../interfaces/userLogin';
import { Meta, Title } from '@angular/platform-browser';
import { PreloaderService } from '../preloader/preloader.service';
import { NotificatorService } from '../../services/notificator.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { environment } from '../../../../environments/environment';

const PASSWORD_MIN_LENGTH = 8;

@Component({
  selector: 'app-root',
  templateUrl: './login.component2.html',
  styleUrls: ['./login.styles.css'],
  animations: [
    // transform: perspective(400px) rotateY(45deg);
    trigger('rotorLogin', [
      transition(':leave', [
        style({ opacity: 1, transform: 'perspective(500px) rotateY(0deg) scale(1)' }),
        animate(
          250,
          style({ opacity: 1, transform: 'perspective(500px) rotateY(90deg) scale(0.65)' }),
        ),
      ]),
      transition(':enter', [
        style({ opacity: 1, transform: 'perspective(500px) rotateY(-90deg) scale(0.65)' }),
        animate(250, style({ opacity: 1, transform: 'perspective(500px) rotateY(0deg) scale(1)' })),
      ]),
    ]),
    trigger('rotorForgot', [
      transition(':leave', [
        style({ opacity: 1, transform: 'perspective(500px) rotateY(0deg) scale(1)' }),
        animate(
          250,
          style({ opacity: 1, transform: 'perspective(500px) rotateY(90deg) scale(0.65)' }),
        ),
      ]),
      transition(':enter', [
        style({ opacity: 1, transform: 'perspective(500px) rotateY(-90deg) scale(0.65)' }),
        animate(250, style({ opacity: 1, transform: 'perspective(500px) rotateY(0deg) scale(1)' })),
      ]),
    ]),
  ],
  standalone: false,
})
export class LoginComponent implements OnInit {
  form: UntypedFormGroup;
  formForgot: UntypedFormGroup;
  formReset: UntypedFormGroup;
  resetPasswordToken: string;
  oneTimePassword: string | null = null;
  submitted = false;
  currentSlide = 'LOGIN';
  showPwd = false;
  readonly passwordMinLength = PASSWORD_MIN_LENGTH;

  showHidePwd() {
    this.showPwd = !this.showPwd;
  }

  /**
   * Slide toggler
   * @param $SlideName -
   */
  goToSlide($SlideName: string) {
    if ($SlideName === 'LOGIN') {
      this.oneTimePassword = null;
    }
    this.currentSlide = '';
    setTimeout(() => {
      this.currentSlide = $SlideName;
    }, 250);
  }

  /**
   * Reset Password
   * first step
   */
  forgot() {
    if (this.formForgot.invalid) {
      return;
    }
    this.submitted = true;
    this.preloader.turnOnPreloader();
    const resetUserPassword = {
      username: this.formForgot.value.usernameForgot,
    };
    this.auth.reset(resetUserPassword).subscribe(
      (data) => {
        // console.log('reset: ', data)
        this.formForgot.reset();
        this.submitted = false;
        this.preloader.turnOffPreloader();
        this.notify.createTopMessage(
          'Please check your Email and follow the instructions',
          'info',
          5000,
        );
        this.goToSlide('LOGIN');
      },
      (error) => {
        console.log('ERROR-A: ', error);
        this.notify.createNotification('error', 'Error', error.error.globalErrors[0], 5);
        this.submitted = false;
        this.preloader.turnOffPreloader();
      },
    );
  }

  /**
   * Reset Password
   * last step
   */
  resetPwd() {
    this.formReset.markAllAsTouched();
    if (this.formReset.invalid) {
      return;
    }
    this.submitted = true;
    this.preloader.turnOnPreloader();
    const updatePassword = {
      token: this.resetPasswordToken,
      password: this.formReset.value.passwordReset1,
    };
    this.auth.resetPassword(updatePassword).subscribe(
      (data) => {
        this.formReset.reset();
        this.submitted = false;
        this.preloader.turnOffPreloader();
        this.notify.createTopMessage('Password has been changed successfully', 'success', 5000);
        this.goToSlide('LOGIN');
      },
      (error) => {
        this.notify.createNotification('error', 'Error', this.apiErrorMessage(error), 5);
        this.preloader.turnOffPreloader();
        this.submitted = false;
      },
    );
  }

  /**
   * Login
   */
  submit() {
    if (this.form.invalid) {
      return;
    }
    this.submitted = true;
    this.preloader.togglePreloader();
    const userLogin: UserLogin = {
      username: this.form.value.username,
      password: this.form.value.password,
    };
    this.auth.login(userLogin).then(
      (data) => {
        if (this.openResetAfterOneTimePassword(data, userLogin.password)) {
          this.submitted = false;
          this.preloader.turnOffPreloader();
          return;
        }
        this.form.reset();
        this.router.navigate(['/dashboard']);
        this.submitted = true;
        this.preloader.togglePreloader();
      },
      (error) => {
        this.notify.createModalNotification('Oooops!...', this.loginErrorMessage(error), 'error');
        this.submitted = false;
        this.preloader.turnOffPreloader();
      },
    );
  }

  constructor(
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private meta: Meta,
    private preloader: PreloaderService,
    private notify: NotificatorService,
  ) {
    // SEO Init
    title.setTitle('Inventory v2 Login Page');
    meta.addTags([
      { name: 'keywords', content: 'Tripium Inventory v2' },
      { name: 'Description', content: 'Tripium Inventory v2 - Login Page' },
    ]);
  }

  ngOnInit() {
    // console.log('D6:init-s')
    this.form = new UntypedFormGroup({
      username: new UntypedFormControl(null, [Validators.required, Validators.minLength(2)]),
      password: new UntypedFormControl(null, [Validators.required, Validators.minLength(2)]),
    });
    this.formForgot = new UntypedFormGroup({
      usernameForgot: new UntypedFormControl(null, [Validators.required, Validators.minLength(2)]),
    });
    this.formReset = this.buildResetForm();
    this.route.params.subscribe((params: Params) => {
      if (environment.stringCheck(params.resetId)) {
        this.resetPasswordToken = params.resetId;
        this.goToSlide('RESET');
      }
    });
    if (this.auth.isAuthenticated()) {
      // console.log('D5:INIT')
      this.auth.updateUserData(this.auth.token);
      this.router.navigate(['/dashboard']);
    }
  }

  protected readonly environment = environment;

  resetPasswordError(controlName: string, errorCode: string): boolean {
    const control = this.formReset?.get(controlName);
    return !!control && control.hasError(errorCode) && (control.dirty || control.touched);
  }

  resetPasswordsMismatch(): boolean {
    return (
      !!this.formReset?.hasError('mismatch') &&
      (this.formReset.get('passwordReset2')?.dirty || this.formReset.get('passwordReset2')?.touched)
    );
  }

  private buildResetForm(): UntypedFormGroup {
    return new UntypedFormGroup(
      {
        passwordReset1: new UntypedFormControl(null, [
          Validators.required,
          Validators.minLength(PASSWORD_MIN_LENGTH),
          (control) => this.notSameAsOneTimePassword(control),
        ]),
        passwordReset2: new UntypedFormControl(null, [
          Validators.required,
          Validators.minLength(PASSWORD_MIN_LENGTH),
        ]),
      },
      { validators: (group) => this.passwordsMatch(group) },
    );
  }

  private openResetAfterOneTimePassword(
    response: HttpResponse<unknown>,
    oneTimePassword: string,
  ): boolean {
    if (!AuthService.isPasswordChangeRequired(response.body)) {
      return false;
    }
    const resetToken = response.body.resetToken;
    this.oneTimePassword = oneTimePassword;
    this.resetPasswordToken = resetToken;
    this.formReset = this.buildResetForm();
    this.goToSlide('RESET');
    this.router.navigate(['/login', resetToken]);
    return true;
  }

  private notSameAsOneTimePassword(control: AbstractControl): ValidationErrors | null {
    if (!this.oneTimePassword || !control.value) {
      return null;
    }
    return control.value === this.oneTimePassword ? { sameAsOneTime: true } : null;
  }

  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const password = group.get('passwordReset1')?.value;
    const confirm = group.get('passwordReset2')?.value;
    if (!password || !confirm) {
      return null;
    }
    return password === confirm ? null : { mismatch: true };
  }

  private loginErrorMessage(error: unknown): string {
    const body = this.errorBody(error);
    return body?.error || 'Login failed';
  }

  private apiErrorMessage(error: unknown): string {
    const body = this.errorBody(error);
    const globalErrors = (body?.globalErrors || []).filter(
      (message) => typeof message === 'string' && !message.startsWith('Correlation ID'),
    );
    return globalErrors[0] || body?.error || 'Failed to reset password';
  }

  private errorBody(error: unknown): {
    error?: string;
    status?: string;
    resetToken?: string;
    globalErrors?: string[];
  } | null {
    if (!error || typeof error !== 'object') {
      return null;
    }
    const body = (error as HttpErrorResponse).error;
    if (body && typeof body === 'object') {
      return body;
    }
    return null;
  }
}
